import { find, isEmpty, map } from 'es-toolkit/compat';
import {
  type SlateImage,
  type SlateImageFailure,
  type SlateImageInput,
  type SlatePublishInput,
  SlatesDocument,
  type SlatesQuery,
  usePublishSlatesMutation,
} from 'generated/graphql';
import { type FC, useState } from 'react';
import { Button, Col, ListGroup, ListGroupItem, Modal, Row } from 'react-bootstrap';
import { stylesheet } from 'typestyle';
import { fontWeightBoldClass } from '../../../app.styles';
import { isLaunchPermOn } from '../../../app.util';
import { getShipmentStatusUrl, getUpdatedSlates } from './slate.util';
import type { SlateFormValue } from './slates.types';

export type SlatePublishModalProps = {
  asin: string;
  marketplace: string;
  isPublishModalOpen: boolean;
  orgUnit: string;
  merchantId: string;
  marketplaceId: number;
  productId: string;
  slateImages: SlateFormValue[];
  publishedData: SlateImage[] | undefined;
  publishedDataLoading: boolean;
  handleModalClose(): void;
};
interface ErroredSlate extends SlateFormValue {
  reason: string;
}

const css = stylesheet({
  dynamicWidthModal: {
    maxWidth: '60%',
  },
  greyText: {
    color: '#737373',
  },
  lineBreak: {
    whiteSpace: 'pre-wrap',
  },
  scrollableList: {
    maxHeight: '10em',
    overflow: 'auto',
  },
});

export const SlatePublishModal: FC<SlatePublishModalProps> = ({
  asin,
  isPublishModalOpen,
  orgUnit,
  merchantId,
  marketplaceId,
  marketplace,
  productId,
  slateImages,
  handleModalClose,
  publishedData,
  publishedDataLoading,
}) => {
  const slatesQueryVariables = {
    asin,
    isLaunchPermOn,
    marketplace,
    productId,
  };
  const [publishSlates] = usePublishSlatesMutation();
  const [shipmentId, setShipmentId] = useState<string>();
  const [published, setPublished] = useState<boolean>();
  const [errorMessage, setErrorMessage] = useState<string>();
  const [slatesErrored, setSlatesErrored] = useState<ErroredSlate[]>([]);

  if (!isPublishModalOpen) {
    return null;
  }
  const prepareForClose = () => {
    setPublished(false);
    setShipmentId('');
    setErrorMessage('');
    setSlatesErrored([]);
    setErrorMessage('');

    handleModalClose();
  };
  const onPublishClicked = () => {
    // these are already filtered to not include removed slates.
    const validSlateImages: SlateImageInput[] = [];
    const failedSlates: SlateImageFailure[] = [];
    slateImages.forEach((slate) => {
      if (slate.url) {
        validSlateImages.push({
          assetId: slate.assetId,
          removed: slate.removed,
          url: slate.url,
          variant: slate.variant,
          version: slate.version,
        });
      } else {
        failedSlates.push({
          reason: 'Variant did not have vaild URL',
          variant: slate.variant,
        });
      }
    });
    const slatePublishInput: SlatePublishInput = {
      asin,
      isLaunchPermOn,
      marketplaceId,
      merchantId: merchantId.toString(),
      ou: orgUnit,
      productId,
      slateImages: validSlateImages,
    };
    publishSlates({
      update: (cache, mutationResult) => {
        if (!mutationResult.data) {
          return;
        }
        const succeededSlates = mutationResult.data.publishSlateImage.succeeded;

        const slatesQueryResult = cache.readQuery({
          query: SlatesDocument,
          variables: slatesQueryVariables,
        }) as SlatesQuery;

        const newCachedSlates = getUpdatedSlates({
          cachedSlates: slatesQueryResult.slates,
          succeededSlates,
        });

        cache.writeQuery({
          data: {
            slates: newCachedSlates,
          },
          query: SlatesDocument,
          variables: slatesQueryVariables,
        });
        failedSlates.push(...mutationResult.data.publishSlateImage.failed);

        setSlatesErrored(
          map(failedSlates, (fSlate: SlateImageFailure) => ({
            ...(find(slateImages, (slate: SlateFormValue) => slate.variant === fSlate.variant) as SlateFormValue),
            reason: fSlate.reason,
          })),
        );

        if (mutationResult.data.publishSlateImage.success) {
          const shipmentId = mutationResult.data.publishSlateImage.shipmentId ?? '';
          setShipmentId(shipmentId);
        } else {
          setErrorMessage(mutationResult.data.publishSlateImage.message);
          console.warn(errorMessage);
        }
        console.log('Mutation result', mutationResult);
      },
      variables: {
        input: slatePublishInput,
      },
    });

    setPublished(true);
  };
  const publishedSlateMap: Record<string, SlateImage> | undefined = publishedData?.reduce(
    (publishedMap, slate) => ({
      // biome-ignore lint/performance/noAccumulatingSpread: legacy code
      ...publishedMap,
      [slate.variant]: slate,
    }),
    {},
  );
  return (
    <div>
      <Modal dialogClassName={css.dynamicWidthModal} onHide={prepareForClose} show={isPublishModalOpen}>
        <Modal.Header closeButton>
          <Modal.Title>Publish slate images</Modal.Title>
        </Modal.Header>
        <Modal.Body className="pb-4 pt-4">
          <div hidden={published}>
            <div className="pt-2 pb-2">The following {slateImages.length} image(s) will be published to Media Central</div>
            <div className="pt-2 pl-3">
              <Row>
                <Col className={fontWeightBoldClass} sm={3}>
                  Variant
                </Col>
                <Col className={fontWeightBoldClass}>Previously published from Journeys</Col>
                <Col className={fontWeightBoldClass}>Changed to</Col>
              </Row>
            </div>
            <ListGroup className={`${css.scrollableList} pb-4`}>
              {slateImages.map((slate, index) => (
                <ListGroupItem key={`${slate.variant}${index}`}>
                  <Row>
                    <Col sm={3}>{slate.variant} </Col>
                    <Col className={css.lineBreak}>
                      {publishedDataLoading ? (
                        <i className="fa fa-spinner fa-pulse fa-3x fa-fw" />
                      ) : (
                        publishedSlateMap?.[slate.variant]?.filename
                      )}
                    </Col>
                    <Col>{slate.filename ?? slate.assetId}</Col>
                  </Row>
                </ListGroupItem>
              ))}
            </ListGroup>
          </div>
          {published ? (
            <div>
              {isEmpty(slatesErrored) ? (
                <div>
                  The {slateImages.length} slate(s) were sent for publishing. You can check the status of your upload here:
                  <a href={getShipmentStatusUrl(shipmentId)} rel="noopener noreferrer" target="_blank">
                    {' '}
                    upload status{' '}
                  </a>
                </div>
              ) : (
                <div>The following {slatesErrored.length} slate(s) failed to publish:</div>
              )}
            </div>
          ) : (
            <>
              <div>
                <div className={`pb-1 ${css.greyText}`}>
                  {' '}
                  The above slate images will be published with the following marketplace related arguments:{' '}
                </div>
                <div>
                  <span className={css.greyText}>Org Unit:</span> <span className={`pr-2 ${css.greyText}`}>{orgUnit}</span>
                </div>
                <div>
                  <span className={css.greyText}>Marketplace ID:</span> <span className={`pr-2 ${css.greyText}`}>{marketplaceId}</span>
                </div>
                <div>
                  <span className={css.greyText}>Merchant ID:</span> <span className={`pr-2 ${css.greyText}`}>{merchantId}</span>
                </div>
              </div>
              <div className="pb-2 pt-3">
                The content you are about to publish may become customer-facing. Please ensure any content you are about to publish is okay
                to publish before proceeding.
              </div>
              <div className={fontWeightBoldClass}>Are you sure you are ready to publish?</div>
            </>
          )}
          <div hidden={isEmpty(slatesErrored)}>
            <div className="pt-2 pl-3">
              <Row>
                <Col className={fontWeightBoldClass} sm={3}>
                  Variant
                </Col>
                <Col className={fontWeightBoldClass}>Filename</Col>
                <Col className={fontWeightBoldClass}>Reason</Col>
              </Row>
            </div>
            <ListGroup className={`${css.scrollableList} pb-4`} hidden={isEmpty(slatesErrored)}>
              {slatesErrored.map((slate) => (
                <ListGroupItem key={slate.variant}>
                  <Row>
                    <Col sm={3}>{slate.variant} </Col>
                    <Col className={css.lineBreak}>{slate.filename}</Col>
                    <Col className={`${css.lineBreak} text-danger`}>{slate.reason}</Col>
                  </Row>
                </ListGroupItem>
              ))}
            </ListGroup>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button hidden={published} onClick={prepareForClose} variant="secondary">
            Cancel
          </Button>
          <Button onClick={published ? prepareForClose : onPublishClicked} variant="primary">
            {published ? 'Ok' : `Yes, publish ${slateImages.length} images`}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};
